import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { useAuthStore, useTenantStore } from './store';
import { applyTenantTheme } from './lib/utils';

// Layout
import AppLayout from './components/AppLayout';
import BPOCanalPage from './pages/BPOCanalPage';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import PayablesPage from './pages/PayablesPage';
import ReceivablesPage from './pages/ReceivablesPage';
import BankReconciliationPage from './pages/BankReconciliationPage';
import CashFlowPage from './pages/CashFlowPage';
import UsersPage from './pages/UsersPage';
import VendorsPage from './pages/VendorsPage';
import CustomersPage from './pages/CustomersPage';
import DREPage from './pages/DREPage';
import ClosedServicesPage from './pages/ClosedServicesPage';
import TaxesPage from './pages/TaxesPage';
import InventoryPage from './pages/InventoryPage';
import CertidoesPage from './pages/CertidoesPage';
import PlanosAssinaturasPage from './pages/PlanosAssinaturasPage';
import OrcamentosPage from './pages/OrcamentosPage';
import BudgetEditorPage from './pages/BudgetEditorPage';
import ProspectEventsPage from './pages/ProspectEventsPage';
import CustomerStatementPage from './pages/CustomerStatementPage';
import VendorStatementPage from './pages/VendorStatementPage';
import FinanceCostCentersPage from './pages/FinanceCostCentersPage';
import CashMovementReportPage from './pages/CashMovementReportPage';
import OpeningBalance2026Page from './pages/OpeningBalance2026Page';
import LegacyDebtsPage from './pages/LegacyDebtsPage';
import ProfilePage from './pages/ProfilePage';
import TenantQuickLoginPage from './pages/TenantQuickLoginPage';
import CashFlowProjectionPage from './pages/CashFlowProjectionPage';
import SalesLeadsPage from './pages/SalesLeadsPage';
import FunilVendasPage from './pages/FunilVendasPage';
import DebtAnalysisPage from './pages/DebtAnalysisPage';
import TarefasPage from './pages/TarefasPage';
import DiscontinuedPage from './pages/DiscontinuedPage';

// Accounting Pages
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import AccountingCostCentersPage from './pages/AccountingCostCentersPage';
import JournalEntriesPage from './pages/JournalEntriesPage';
import PeriodClosuresPage from './pages/PeriodClosuresPage';
import AccountingReportsPage from './pages/AccountingReportsPage';

// Editorial Pages
import EditorialDashboardPage from './pages/EditorialDashboardPage';
import PostEditorPage from './pages/PostEditorPage';
import MediaLibraryPage from './pages/MediaLibraryPage';

// HR Pages
import EmployeesPage from './pages/EmployeesPage';
import ContractorsPage from './pages/ContractorsPage';
import DailyClosurePage from './pages/DailyClosurePage';
import SelectCompanyPage from './pages/SelectCompanyPage';
import PayrollPage from './pages/PayrollPage';
import PayrollEmployeeHistoryPage from './pages/PayrollEmployeeHistoryPage';
import PayrollEmployeesPage from './pages/PayrollEmployeesPage';
import PayrollPartnersPage from './pages/PayrollPartnersPage';
import PayrollYearlyPage from './pages/PayrollYearlyPage';
import SecurityPage from './pages/SecurityPage';
import CategoryReportPage from './pages/CategoryReportPage';
import ReclassifyPage from './pages/ReclassifyPage';
import BalancetePage from './pages/BalancetePage';
import KpiDashboardPage from './pages/KpiDashboardPage';
import SanitizePage from './pages/SanitizePage';
import LastImportErrorPage from './pages/LastImportErrorPage';
import CommercialMonthlyReportPage from './pages/CommercialMonthlyReportPage';
import BulkCategorizePage from './pages/BulkCategorizePage';

// Logistics Pages
import LogisticsAgendaPage from './pages/LogisticsAgendaPage';
import LogisticsListPage from './pages/LogisticsListPage';
import EventDetailPage from './pages/EventDetailPage';
import WorkOrdersListPage from './pages/WorkOrdersListPage';
import WorkOrderDetailPage from './pages/WorkOrderDetailPage';

// Credit Cards & Strategic Planning
import CreditCardsPage from './pages/CreditCardsPage';
import StrategicPlanPage from './pages/StrategicPlanPage';
import CategoriesPage from './pages/CategoriesPage';

// Admin Pages
import BrandingPage from './pages/BrandingPage';
import BackupPage from './pages/BackupPage';

// Admin
import AdminTenantsPage from './pages/AdminTenantsPage';
import SuperAdminPage from './pages/SuperAdminPage';

// Tax Simulation
import TaxSimulationPage from './pages/TaxSimulationPage';

// Monthly Report
import MonthlyReportPage from './pages/MonthlyReportPage';

// Help Page
import HelpPage from './pages/HelpPage';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, token, fetchMe } = useAuthStore();
  const { tenant } = useAuthStore();

  useEffect(() => {
    if (token && !isAuthenticated) {
      fetchMe();
    }
  }, [token, isAuthenticated, fetchMe]);

  useEffect(() => {
    if (tenant?.branding) {
      applyTenantTheme(tenant.branding);
    }
  }, [tenant]);

  if (!token) {
    return <Navigate to="/login" replace />;
  }

  return <AppLayout>{children}</AppLayout>;
};

// Admin-only Route - redirects assistente to their default page
const AdminRoute = ({ children }) => {
  const { user } = useAuthStore();
  if (user?.role === 'assistente') {
    return <Navigate to="/comercial/orcamentos" replace />;
  }
  return children;
};

// Smart redirect based on role
const RoleBasedRedirect = () => {
  const { user } = useAuthStore();
  if (user?.role === 'assistente') {
    return <Navigate to="/comercial/orcamentos" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

// Placeholder Page
const PlaceholderPage = ({ title }) => (
  <div className="flex flex-col items-center justify-center h-64 text-center">
    <h2 className="text-2xl font-bold mb-2">{title}</h2>
    <p className="text-muted-foreground">Esta funcionalidade será implementada em breve.</p>
  </div>
);

const SYSTEM_DISCONTINUED = true;

function App() {
  if (SYSTEM_DISCONTINUED) {
    return <DiscontinuedPage />;
  }
  return (
    <>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/selecionar-empresa" element={<SelectCompanyPage />} />
          {/* Deep-link por empresa: /t/:slug -> pula seleção e vai direto pro login */}
          <Route path="/t/:slug" element={<TenantQuickLoginPage />} />
          <Route path="/login" element={<LoginPage />} />
          
          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <AdminRoute><DashboardPage /></AdminRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/home"
            element={
              <ProtectedRoute>
                <PlaceholderPage title="Home" />
              </ProtectedRoute>
            }
          />

          {/* Finance Routes */}
          <Route
            path="/financeiro/conciliacao"
            element={
              <ProtectedRoute>
                <BankReconciliationPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/pagar"
            element={
              <ProtectedRoute>
                <PayablesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/receber"
            element={
              <ProtectedRoute>
                <ReceivablesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/fluxo-caixa"
            element={
              <ProtectedRoute>
                <AdminRoute><CashFlowPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/categorias"
            element={
              <ProtectedRoute>
                <CategoriesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/contas"
            element={
              <ProtectedRoute>
                <BankReconciliationPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/fornecedores"
            element={
              <ProtectedRoute>
                <VendorsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/clientes"
            element={
              <ProtectedRoute>
                <CustomersPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/dre"
            element={
              <ProtectedRoute>
                <AdminRoute><DREPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/servicos-fechados"
            element={
              <ProtectedRoute>
                <ClosedServicesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/tributos"
            element={
              <ProtectedRoute>
                <TaxesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/inventario"
            element={
              <ProtectedRoute>
                <AdminRoute><InventoryPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/certidoes"
            element={
              <ProtectedRoute>
                <CertidoesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/planos"
            element={
              <ProtectedRoute>
                <AdminRoute><PlanosAssinaturasPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/cartoes"
            element={
              <ProtectedRoute>
                <AdminRoute><CreditCardsPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/estrategia"
            element={
              <ProtectedRoute>
                <StrategicPlanPage />
              </ProtectedRoute>
            }
          />

          {/* Accounting Routes */}
          <Route
            path="/contabilidade/plano-contas"
            element={
              <ProtectedRoute>
                <ChartOfAccountsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contabilidade/centros-custo"
            element={
              <ProtectedRoute>
                <AccountingCostCentersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contabilidade/lancamentos"
            element={
              <ProtectedRoute>
                <JournalEntriesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contabilidade/fechamentos"
            element={
              <ProtectedRoute>
                <PeriodClosuresPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/contabilidade/relatorios"
            element={
              <ProtectedRoute>
                <AccountingReportsPage />
              </ProtectedRoute>
            }
          />

          {/* Editorial Routes */}
          <Route
            path="/editorial"
            element={
              <ProtectedRoute>
                <EditorialDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editorial/posts/:postId"
            element={
              <ProtectedRoute>
                <PostEditorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/editorial/media"
            element={
              <ProtectedRoute>
                <MediaLibraryPage />
              </ProtectedRoute>
            }
          />

          {/* Commercial Routes */}
          <Route
            path="/comercial/orcamentos"
            element={
              <ProtectedRoute>
                <OrcamentosPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/orcamentos/:id"
            element={
              <ProtectedRoute>
                <BudgetEditorPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/comercial/pipeline"
            element={
              <ProtectedRoute>
                <SalesLeadsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/comercial/funil"
            element={
              <ProtectedRoute>
                <FunilVendasPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/comercial/prospeccao"
            element={
              <ProtectedRoute>
                <ProspectEventsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/clientes/:customerId/extrato"
            element={
              <ProtectedRoute>
                <CustomerStatementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/fornecedores/:vendorId/extrato"
            element={
              <ProtectedRoute>
                <VendorStatementPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/centros-de-custo"
            element={
              <ProtectedRoute>
                <FinanceCostCentersPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/relatorios/movimento-caixa"
            element={
              <ProtectedRoute>
                <CashMovementReportPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/balanco-abertura-2026"
            element={
              <ProtectedRoute>
                <OpeningBalance2026Page />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/dividas-anteriores"
            element={
              <ProtectedRoute>
                <LegacyDebtsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/financeiro/relatorios/fluxo-caixa-projetado"
            element={
              <ProtectedRoute>
                <CashFlowProjectionPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/financeiro/analise-dividas"
            element={
              <ProtectedRoute>
                <AdminRoute><DebtAnalysisPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tarefas"
            element={
              <ProtectedRoute>
                <TarefasPage />
              </ProtectedRoute>
            }
          />

          {/* Logistics Routes */}
          <Route
            path="/logistica"
            element={
              <ProtectedRoute>
                <LogisticsAgendaPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/lista"
            element={
              <ProtectedRoute>
                <LogisticsListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/logistica/eventos/:eventId"
            element={
              <ProtectedRoute>
                <EventDetailPage />
              </ProtectedRoute>
            }
          />

          {/* Work Orders Routes */}
          <Route
            path="/os"
            element={
              <ProtectedRoute>
                <WorkOrdersListPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/os/:woId"
            element={
              <ProtectedRoute>
                <WorkOrderDetailPage />
              </ProtectedRoute>
            }
          />

          {/* HR Routes */}
          <Route
            path="/rh/funcionarios"
            element={
              <ProtectedRoute>
                <EmployeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/terceiros"
            element={
              <ProtectedRoute>
                <ContractorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/fechamento-diario"
            element={
              <ProtectedRoute>
                <DailyClosurePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha"
            element={
              <ProtectedRoute>
                <PayrollPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha/funcionarios"
            element={
              <ProtectedRoute>
                <PayrollEmployeesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha/funcionario/:employeeId"
            element={
              <ProtectedRoute>
                <PayrollEmployeeHistoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha/socios"
            element={
              <ProtectedRoute>
                <PayrollPartnersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha/socios/:partnerId"
            element={
              <ProtectedRoute>
                <PayrollPartnersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/folha/relatorio-anual"
            element={
              <ProtectedRoute>
                <PayrollYearlyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/seguranca"
            element={
              <ProtectedRoute>
                <SecurityPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/relatorios/por-categoria"
            element={
              <ProtectedRoute>
                <CategoryReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/reclassificar-centros"
            element={
              <ProtectedRoute>
                <ReclassifyPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/balancete"
            element={
              <ProtectedRoute>
                <BalancetePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/kpi-dashboard"
            element={
              <ProtectedRoute>
                <KpiDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/sanitize-amounts"
            element={
              <ProtectedRoute>
                <SanitizePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/last-import-error"
            element={
              <ProtectedRoute>
                <LastImportErrorPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/comercial/relatorio-mensal"
            element={
              <ProtectedRoute>
                <CommercialMonthlyReportPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/financeiro/categorizar-massa"
            element={
              <ProtectedRoute>
                <BulkCategorizePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/rh/*"
            element={
              <ProtectedRoute>
                <PlaceholderPage title="RH" />
              </ProtectedRoute>
            }
          />

          {/* Admin Routes */}
          <Route
            path="/admin/usuarios"
            element={
              <ProtectedRoute>
                <AdminRoute><UsersPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/auditoria"
            element={
              <ProtectedRoute>
                <PlaceholderPage title="Auditoria" />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/branding"
            element={
              <ProtectedRoute>
                <AdminRoute><BrandingPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin/backup"
            element={
              <ProtectedRoute>
                <AdminRoute><BackupPage /></AdminRoute>
              </ProtectedRoute>
            }
          />
          
          {/* Admin - Gerenciar Clientes (apenas atomaslau) */}
          <Route
            path="/admin/clientes"
            element={
              <ProtectedRoute>
                <AdminRoute><AdminTenantsPage /></AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Super Admin - Administração Geral */}
          <Route
            path="/admin/geral"
            element={
              <ProtectedRoute>
                <AdminRoute><SuperAdminPage /></AdminRoute>
              </ProtectedRoute>
            }
          />

          {/* Tax Simulation */}
          <Route
            path="/simulacao-fiscal"
            element={
              <ProtectedRoute>
                <AdminRoute><TaxSimulationPage /></AdminRoute>
              </ProtectedRoute>
            }
          />

          <Route
            path="/relatorio-mensal"
            element={
              <ProtectedRoute>
                <MonthlyReportPage />
              </ProtectedRoute>
            }
          />

          {/* Profile */}
          <Route
            path="/perfil"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />

          {/* Help */}
          <Route
            path="/ajuda"
            element={
              <ProtectedRoute>
                <HelpPage />
              </ProtectedRoute>
            }
          />

          {/* Canal BPO — ServiceDesk interno */}
          <Route
            path="/bpo-canal"
            element={
              <ProtectedRoute>
                <BPOCanalPage />
              </ProtectedRoute>
            }
          />

          {/* Catch all */}
          <Route path="*" element={<ProtectedRoute><RoleBasedRedirect /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </>
  );
}

export default App;
